import React from "react";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import dayjs from "~/config/dayjs";
import Utils from "~/helpers/Utils";

/* ── paleta ── */
const P      = "#8C1837";
const P_SOFT = "#F7EDEF";
const P_BD   = "#EBD3D8";
const INK    = "#2B2B30";
const BODY   = "#6B6B72";
const MUT    = "#8A8A90";
const LIN    = "#EDEDE6";

const CAFE     = "#2A1F1A";
const CAFE_ESC = "#171110";
const CAFE_CLARO = "#3C2C24";

/* ── gradiente metálico (cartão ativo) ── */
const metal = (a, b, c) => [
	"radial-gradient(150% 130% at 100% 118%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.22) 26%, rgba(255,255,255,0.06) 38%, rgba(255,255,255,0) 52%)",
	"radial-gradient(80% 90% at -5% -15%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 60%)",
	"repeating-linear-gradient(118deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0) 2px, rgba(0,0,0,0.06) 4px, rgba(255,255,255,0) 6px)",
	"linear-gradient(145deg, " + a + " 0%, " + b + " 52%, " + c + " 100%)",
].join(", ");

/* ── ícones inline ── */
const ChevronRight = ({ color = "rgba(255,255,255,0.7)" }) => (
	<svg width="7" height="11" viewBox="0 0 7 11" fill="none"
		stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
		<polyline points="1,1 6,5.5 1,10" />
	</svg>
);

/* ── card de pacote (v19) ── */
const PackageCard = ({ group, business, onPayNow, onAgendar, businessesPolicyCancellation = [] }) => {
	const { t } = useTranslation();
	const currency = business?.currency || "BRL";

	const {
		bookingTransactionId,
		serviceName,
		totalSessions,
		scheduledSessions,
		totalPaid,
		paymentStatus,
		package: pkg,
		sessions = [],
		businessName,
	} = group;

	/* ── estado ── */
	const targetBizId = group?.businessId || group?.business?.id || business?.id;
	const businessPolicy = businessesPolicyCancellation.find((p) => p.business_id === targetBizId);
	const isPolicyCancellation = businessPolicy?.isPolicyCancellation ?? false;
	const pend = paymentStatus === "pending" && isPolicyCancellation;
	const fim  = scheduledSessions >= totalSessions && totalSessions > 0;

	const rawExpiresAt = sessions?.[0]?.expiresAt
		? dayjs(sessions[0].expiresAt)
		: pkg?.bookingExpirationDays != null
			? dayjs().add(pkg.bookingExpirationDays, "day")
			: null;

	const expiresAt  = rawExpiresAt ? rawExpiresAt.format("DD/MM/YYYY") : null;
	const venceu     = !pend && !fim && rawExpiresAt != null && rawExpiresAt.isBefore(dayjs(), "day");
	const vivo = !pend && !fim && !venceu;

	/* ── dados de exibição ── */
	const packageName = pkg?.name || serviceName || "";
	const displayBiz  = businessName || business?.name || "";
	const restam      = totalSessions - scheduledSessions;

	const totalPrice = totalPaid != null
		? totalPaid
		: sessions.reduce((s, x) => s + (x.price || 0), 0);

	/* ────────────────────────────────────────────
	   ATIVO — cartão de metal escuro
	──────────────────────────────────────────── */
	if (vivo) {
		return (
			<div style={{
				background: metal(CAFE_CLARO, CAFE, CAFE_ESC),
				borderRadius: 16,
				padding: "12px 14px",
				marginBottom: 9,
				cursor: "pointer",
				position: "relative",
				overflow: "hidden",
			}}>
				{/* Header: nome do negócio + chevron */}
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
					<span style={{
						fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
						textTransform: "uppercase", color: "rgba(255,255,255,0.8)",
					}} translate="no">
						{displayBiz}
					</span>
					<ChevronRight />
				</div>

				{/* Nome do pacote */}
				<div style={{
					fontSize: 14.5, fontWeight: 700, color: "#fff",
					marginTop: 7, lineHeight: 1.3,
					whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
				}} translate="no">
					{packageName}
				</div>

				{/* Sessões + botão Agendar */}
				<div style={{ marginTop: 9, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
					<div style={{ minWidth: 0 }}>
						<div style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
							<span style={{ fontSize: 28, fontWeight: 800, color: "#fff", letterSpacing: -1, lineHeight: 1 }}>
								{restam}
							</span>
							<span style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.88)" }}>
								{restam === 1 ? t("disponível") : t("disponíveis")} {t("de")} {totalSessions}
							</span>
						</div>
						<div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", marginTop: 4 }}>
							{expiresAt
								? t("reservas_packages_expires", { date: expiresAt })
								: t("No expiration")}
						</div>
					</div>
					<button
						onClick={() => onAgendar && onAgendar(group, targetBizId)}
						style={{
							flexShrink: 0,
							background: "#fff",
							border: "none",
							color: CAFE_ESC,
							borderRadius: 999,
							padding: "10px 16px",
							fontSize: 13,
							fontWeight: 800,
							fontFamily: "inherit",
							cursor: "pointer",
							whiteSpace: "nowrap",
						}}>
						{t("Agendar")}
					</button>
				</div>
			</div>
		);
	}

	/* ────────────────────────────────────────────
	   PENDENTE — fundo rosa suave
	──────────────────────────────────────────── */
	if (pend) {
		return (
			<div style={{
				background: P_SOFT,
				border: "1px solid " + P_BD,
				borderRadius: 16,
				padding: "12px 14px",
				marginBottom: 9,
				position: "relative",
				overflow: "hidden",
			}}>
				{/* Nome do negócio */}
				<span style={{
					fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
					textTransform: "uppercase", color: P,
				}} translate="no">
					{displayBiz}
				</span>

				{/* Nome do pacote */}
				<div style={{
					fontSize: 14.5, fontWeight: 700, color: INK,
					marginTop: 7, lineHeight: 1.3,
					whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
				}} translate="no">
					{packageName}
				</div>

				{/* Valor + botão Pagar agora */}
				<div style={{ marginTop: 9, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
					<div style={{ minWidth: 0 }}>
						<span style={{
							fontSize: 24, fontWeight: 800, color: INK,
							letterSpacing: -0.8, lineHeight: 1.05, display: "block",
						}}>
							{Utils.formatMoney(totalPrice / 100, currency)}
						</span>
						<span style={{ fontSize: 12.5, color: BODY }}>
							{totalSessions} {t("reservas_packages_sessions_label")} · {t("reservas_packages_pending_short")}
						</span>
					</div>
					<button
						onClick={() => onPayNow && onPayNow(bookingTransactionId, totalPrice, packageName)}
						style={{
							flexShrink: 0,
							background: P,
							border: "none",
							color: "#fff",
							borderRadius: 999,
							padding: "10px 16px",
							fontSize: 13,
							fontWeight: 800,
							fontFamily: "inherit",
							cursor: "pointer",
							whiteSpace: "nowrap",
						}}>
						{t("reservas_packages_pay_now")}
					</button>
				</div>
			</div>
		);
	}

	/* ────────────────────────────────────────────
	   ENCERRADO — fundo branco, tinta apagada
	──────────────────────────────────────────── */
	return (
		<div style={{
			background: "#fff",
			border: "1px solid " + LIN,
			borderRadius: 16,
			padding: "12px 14px",
			marginBottom: 9,
			position: "relative",
			overflow: "hidden",
		}}>
			{/* Header: nome do negócio + chevron */}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
				<span style={{
					fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
					textTransform: "uppercase", color: MUT,
				}} translate="no">
					{displayBiz}
				</span>
				<ChevronRight color="#C4C6CC" />
			</div>

			{/* Nome do pacote */}
			<div style={{
				fontSize: 14.5, fontWeight: 700, color: BODY,
				marginTop: 7, lineHeight: 1.3,
				whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
			}} translate="no">
				{packageName}
			</div>

			{/* Status */}
			<span style={{ fontSize: 13.5, fontWeight: 700, color: MUT, display: "block", marginTop: 9 }}>
				{fim
					? t("reservas_packages_closed_used", { total: totalSessions })
					: t("reservas_packages_closed_unused", { count: restam })}
			</span>
			<span style={{ fontSize: 12.5, color: MUT, marginTop: 2, display: "block" }}>
				{fim
					? t("reservas_packages_concluded")
					: t("reservas_packages_expired_on", { date: expiresAt })}
			</span>
		</div>
	);
};

/* ── prioridade de exibição: ativos > pendentes > encerrados ── */
const packagePriority = (group) => {
	const pend = group.paymentStatus === "pending";
	const fim  = group.scheduledSessions >= group.totalSessions && group.totalSessions > 0;
	const rawExpires = group.sessions?.[0]?.expiresAt
		? dayjs(group.sessions[0].expiresAt)
		: group.package?.bookingExpirationDays != null
			? dayjs().add(group.package.bookingExpirationDays, "day")
			: null;
	const venceu = !pend && !fim && rawExpires != null && rawExpires.isBefore(dayjs(), "day");
	if (!pend && !fim && !venceu) return 0; // ativo
	if (pend)                               return 1; // pendente
	return 2;                                         // encerrado
};

/* ── lista ── */
const ListPackagesGrouped = ({ packages, business, onPayNow, onAgendar, loadMorePackages, businessesPolicyCancellation = [] }) => {
	if (!packages || packages.length === 0) return null;

	const sorted = [...packages].sort((a, b) => packagePriority(a) - packagePriority(b));

	return (
		<div style={{ padding: "12px 16px 60px" }}>
			{sorted.map((group) => (
				<PackageCard
					key={group.bookingTransactionId}
					group={group}
					business={business}
					onPayNow={onPayNow}
					onAgendar={onAgendar}
					businessesPolicyCancellation={businessesPolicyCancellation}
				/>
			))}
			{loadMorePackages && (
				<div style={{ marginBottom: 50 }}>{loadMorePackages()}</div>
			)}
		</div>
	);
};

export default withRouter(ListPackagesGrouped);
